<template>
  <RevDrawer
    :close-button-label="i18n(translations.close)"
    large
    :name="MODAL_NAME"
    :title="i18n(translations.modalTitle)"
    @close="resetQueryParams"
    @open="refreshSellerFiles"
  >
    <template #body>
      <RevLoadingScreen
        v-if="isLoading"
        class="mt-32"
        :text="i18n(translations.loadingText)"
      />
      <template v-if="!isLoading">
        <div class="w-full overflow-x-scroll">
          <TheFileLibraryFileTable
            v-if="fileList.length"
            :file-list
            @seller-file-deleted="refreshSellerFiles"
          />
          <Pagination
            class="mt-56"
            :current-page
            :page-count
            @click="handlePaginationClick"
          />
        </div>
        <RevContentBlock
          v-if="!fileList.length"
          class="px-32 py-12"
          :image-props="{
            alt: i18n(translations.noFileTitle),
            height: 257,
            src: '/img/empty.svg',
            width: 465,
          }"
          :surtitle="i18n(translations.noFileDescription)"
          :title="i18n(translations.noFileTitle)"
        />
      </template>
    </template>
  </RevDrawer>
</template>

<script lang="ts" setup>
import {
  type Error,
  getApiErrorMessage,
  navigateTo,
  usePermissions,
  useRoute,
} from '#imports'
import { computed, ref, watch } from 'vue'

import { merchantsAPI } from '@backmarket/http-api'
import { useHttpFetch } from '@backmarket/nuxt-module-http/useHttpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { RevContentBlock } from '@ds/components/ContentBlock'
import { RevDrawer } from '@ds/components/Drawer'
import { RevLoadingScreen } from '@ds/components/LoadingScreen'
import { openModal } from '@ds/components/ModalBase'

import { SCOPES } from '~/constants/permissions'

import translations from './TheFileLibrary.translations'

const MODAL_NAME = 'FileLibraryModal'
const i18n = useI18n()
const { openErrorToast } = useTheToast()
const currentRoute = useRoute()
const { getSellerFiles } = merchantsAPI
const currentPage = ref(1)
const { hasScopeAccess } = usePermissions()

const hasFileLibraryPermission = computed(() =>
  hasScopeAccess(SCOPES.FILE_LIBRARY.name),
)

const {
  data: sellerFilesPayload,
  error: sellerFilesError,
  refresh: refreshSellerFiles,
  pending: isLoading,
} = useHttpFetch(getSellerFiles, {
  queryParams: { page: currentPage },
  watch: [currentPage],
})

const fileList = computed(() => sellerFilesPayload.value?.results ?? [])
const fileCount = computed(() => sellerFilesPayload.value?.count ?? 0)

watch(
  sellerFilesError,
  (error) => {
    if (error) {
      openErrorToast({
        content: getApiErrorMessage(error as unknown as Error),
      })
    }
  },
  {
    immediate: true,
  },
)
const openFileLibraryModal = () => {
  if (
    Boolean(currentRoute.query['file-library']) === true &&
    hasFileLibraryPermission
  ) {
    openModal(MODAL_NAME)
  }
}

watch(() => currentRoute.path, openFileLibraryModal, {
  immediate: true,
  deep: true,
})

const resetQueryParams = async () => {
  const newQuery = { ...currentRoute.query }

  delete newQuery['file-library']

  await navigateTo({
    ...currentRoute,
    query: {
      ...newQuery,
    },
    replace: true,
  })
}

const pageCount = computed(() => Math.ceil(fileCount.value / 10))

function handlePaginationClick({ page }: { page: number }) {
  currentPage.value = page
}
</script>
